import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { db, auth } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";

import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import logoImg from "../assets/images/craic-on-the-case-logo.jpg";
import confetti from "canvas-confetti";
import "./StartTrail.css";
import "./SuccessImageCelebration.css";

export default function StartTrail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const [authReady, setAuthReady] = useState(false);
  const [trail, setTrail] = useState(null);
  const [clues, setClues] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [solvedClues, setSolvedClues] = useState([]);

  const [userAnswer, setUserAnswer] = useState("");
  const [correct, setCorrect] = useState(false);

  const [loading, setLoading] = useState(true);
  const [popupImage, setPopupImage] = useState(null);
  const [showHint, setShowHint] = useState(false);

  const [showTipModal, setShowTipModal] = useState(false);
  const [currentTip, setCurrentTip] = useState("");
  const [currentTipClue, setCurrentTipClue] = useState(null);
  const [showHelpWarning, setShowHelpWarning] = useState(false);
  const [showSuccessImage, setShowSuccessImage] = useState(false);
  const [successImageUrl, setSuccessImageUrl] = useState("");

  const [startedAt, setStartedAt] = useState(null);
  const [completedAt, setCompletedAt] = useState(null);
  const [activeTime, setActiveTime] = useState(0);
  const [activeStart, setActiveStart] = useState(null);

  const restartRequested = searchParams.get("restart") === "true";

  const normalize = (str) =>
    str.toLowerCase().replace(/[^\w\s]/g, "").trim();

  const answerMatch = (user, correctAns) => {
    const u = normalize(user).split(" ");
    const c = normalize(correctAns).split(" ");
    return c.every((w) => u.includes(w));
  };

  const extractNextLocation = (cl) =>
    cl.nextlocation || cl.nextLocation || null;

  const parseCoords = (value) => {
    if (!value) return null;
    const p = value.split(",");
    if (p.length !== 2) return null;
    return { lat: p[0].trim(), lng: p[1].trim() };
  };

  const buildMapLink = (c) =>
    `https://maps.google.com/maps?q=${c.lat},${c.lng}&z=15`;

  const buildMapEmbed = (c) =>
    `https://maps.google.com/maps?q=${c.lat},${c.lng}&z=15&output=embed`;

  const formatDuration = (ms) => {
    if (!ms || ms <= 0) return "0m 0s";
    let totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds -= hours * 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds - minutes * 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return parts.join(" ");
  };

  const generateCertificateId = () => {
    const ts = Date.now().toString(36);
    const rand = Math.random().toString(36).substring(2, 8);
    return `CTC-${ts}-${rand}`.toUpperCase();
  };

  const generateCertificate = () => {
    const userName =
      auth.currentUser?.displayName ||
      window.prompt("Enter your name for the certificate:", "Detective");

    if (!userName) return;

    const trailName = trail?.name || "Mystery Trail";

    const completionDateObj = completedAt
      ? new Date(completedAt)
      : new Date();

    const completionDate = completionDateObj.toLocaleDateString();
    const certId = generateCertificateId();

    const docPdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = docPdf.internal.pageSize.getWidth();
    const pageHeight = docPdf.internal.pageSize.getHeight();

    const darkGreen = { r: 22, g: 34, b: 32 };
    const midGreen = { r: 35, g: 55, b: 50 };
    const gold = { r: 246, g: 201, b: 71 };

    docPdf.setFillColor(darkGreen.r, darkGreen.g, darkGreen.b);
    docPdf.rect(0, 0, pageWidth, pageHeight, "F");

    const margin = 40;
    docPdf.setFillColor(midGreen.r, midGreen.g, midGreen.b);
    docPdf.roundedRect(
      margin,
      margin,
      pageWidth - margin * 2,
      pageHeight - margin * 2,
      22,
      22,
      "F"
    );

    const logoAreaWidth = pageWidth * 0.35;
    const textAreaX = margin + logoAreaWidth + 20;

    const img = new Image();
    img.src = logoImg;

    img.onload = () => {
      const maxWidth = logoAreaWidth - margin * 1.5;
      const ratio = img.height / img.width;
      const imgW = maxWidth;
      const imgH = imgW * ratio;

      const imgX = margin + (logoAreaWidth - imgW) / 2;
      const imgY = (pageHeight - imgH) / 2;

      docPdf.addImage(img, "JPEG", imgX, imgY, imgW, imgH);

      docPdf.setTextColor(gold.r, gold.g, gold.b);
      docPdf.setFont("helvetica", "bold");
      docPdf.setFontSize(36);
      docPdf.text("Certificate of Completion", textAreaX, margin + 60);

      docPdf.setTextColor(255, 255, 255);
      docPdf.setFont("helvetica", "normal");
      docPdf.setFontSize(18);
      docPdf.text("This certifies that", textAreaX, margin + 110);

      docPdf.setFont("helvetica", "bold");
      docPdf.setFontSize(28);
      docPdf.text(userName, textAreaX, margin + 150);

      docPdf.setFont("helvetica", "normal");
      docPdf.setFontSize(18);
      docPdf.text("has successfully completed the trail", textAreaX, margin + 190);

      docPdf.setFont("helvetica", "bold");
      docPdf.setFontSize(24);
      docPdf.text(`"${trailName}"`, textAreaX, margin + 225);

      docPdf.setFont("helvetica", "normal");
      docPdf.setFontSize(16);
      docPdf.text(`Completed on: ${completionDate}`, textAreaX, margin + 270);

      docPdf.setFontSize(12);
      docPdf.setTextColor(220, 220, 220);
      docPdf.text(
        `Certificate ID: ${certId}`,
        textAreaX,
        pageHeight - margin - 30
      );

      docPdf.setFontSize(10);
      docPdf.text(
        "Craic on the Case — A Murder Mystery Walking Trail",
        textAreaX,
        pageHeight - margin - 12
      );

      const safeName = trailName.replace(/[^a-z0-9]+/gi, "-");
      docPdf.save(`certificate-${safeName}.pdf`);
    };
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(() => setAuthReady(true));
    return unsub;
  }, []);

  useEffect(() => {
    async function loadTrail() {
      const snap = await getDoc(doc(db, "trails", id));
      if (snap.exists()) setTrail(snap.data());
    }
    loadTrail();
  }, [id]);

  useEffect(() => {
    async function loadClues() {
      try {
        const q = query(
          collection(db, "clues"),
          where("trailId", "==", id),
          orderBy("order", "asc")
        );
        const snap = await getDocs(q);
        setClues(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    }
    loadClues();
  }, [id]);

  useEffect(() => {
    if (!auth.currentUser) return;
    if (!clues.length) return;
    if (restartRequested) return;

    async function loadProgress() {
      const ref = doc(db, "progress", `${auth.currentUser.uid}_${id}`);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;

      const data = snap.data();

      const safeIndex = Math.min(data.currentIndex ?? 0, clues.length);
      setCurrentIndex(safeIndex);
      setSolvedClues(data.solvedClues || []);
      setStartedAt(data.startedAt ?? null);
      setCompletedAt(data.completedAt ?? null);
      setActiveTime(data.activeTime ?? 0);

      if (safeIndex < clues.length) {
        setActiveStart(Date.now());
      } else {
        setActiveStart(null);
      }
    }

    loadProgress();
  }, [clues, id, restartRequested]);

  async function autoSave(
    newIndex,
    updatedClues,
    startedAtVal,
    completedAtVal,
    activeTimeVal
  ) {
    if (!auth.currentUser) return;
    const ref = doc(db, "progress", `${auth.currentUser.uid}_${id}`);

    await setDoc(ref, {
      userId: auth.currentUser.uid,
      trailId: id,
      currentIndex: newIndex,
      solvedClues: updatedClues,
      startedAt: startedAtVal ?? null,
      completedAt: completedAtVal ?? null,
      activeTime: activeTimeVal ?? 0,
      lastUpdated: Date.now(),
    });
  }

  const showStartButton = !startedAt && currentIndex === 0;

  async function handleStartTimer() {
    const now = Date.now();

    const newStartedAt = now;
    const newActiveTime = activeTime ?? 0;

    setStartedAt(newStartedAt);
    setCompletedAt(null);
    setActiveTime(newActiveTime);
    setActiveStart(now);

    if (auth.currentUser) {
      const ref = doc(db, "progress", `${auth.currentUser.uid}_${id}`);
      await setDoc(
        ref,
        {
          userId: auth.currentUser.uid,
          trailId: id,
          currentIndex,
          solvedClues,
          startedAt: newStartedAt,
          completedAt: null,
          activeTime: newActiveTime,
          lastUpdated: Date.now(),
        },
        { merge: true }
      );
    }
  }

  function submitAnswer(e) {
    e.preventDefault();

    if (!answerMatch(userAnswer, clues[currentIndex].answer)) {
      alert("Incorrect — try again!");
      return;
    }

    const now = Date.now();
    let effectiveStartedAt = startedAt;
    let newActiveTime = activeTime ?? 0;
    let newActiveStart = activeStart;

    if (!effectiveStartedAt) {
      effectiveStartedAt = now;
      newActiveStart = now;
    }

    if (newActiveStart) {
      newActiveTime += now - newActiveStart;
    }

    const clue = clues[currentIndex];

    const updatedSolved = [
      ...solvedClues,
      {
        id: clue.id,
        order: clue.order,
        answer: clue.answer,
        helpfultip: clue.helpfultip || "",
        nextlocation: extractNextLocation(clue),
      },
    ];

    const nextIndex = currentIndex + 1;

    let newCompletedAt = completedAt;
    if (nextIndex >= clues.length) {
      newCompletedAt = now;
      newActiveStart = null;
    } else {
      newActiveStart = now;
    }

    setSolvedClues(updatedSolved);
    setStartedAt(effectiveStartedAt);
    setCompletedAt(newCompletedAt);
    setActiveTime(newActiveTime);
    setActiveStart(newActiveStart);

    setCorrect(true);
    confetti({ particleCount: 120, spread: 70 });

    /** SHOW SUCCESS IMAGE IF AVAILABLE **/
    if (clue.successImage && clue.successImage.trim() !== "") {
      setSuccessImageUrl(clue.successImage);
      setShowSuccessImage(true);
      
      // Extra confetti burst for success image!
      setTimeout(() => {
        confetti({ 
          particleCount: 200, 
          spread: 100,
          origin: { y: 0.6 }
        });
      }, 300);
      
      setTimeout(() => {
        confetti({ 
          particleCount: 150, 
          angle: 60,
          spread: 55,
          origin: { x: 0 }
        });
        confetti({ 
          particleCount: 150, 
          angle: 120,
          spread: 55,
          origin: { x: 1 }
        });
      }, 600);
    }

    /** AUTO-SHOW TIP **/
    if (clue.helpfultip && clue.helpfultip.trim() !== "") {
      setCurrentTip(clue.helpfultip);
      setCurrentTipClue(clue.order);
      setShowTipModal(true);
    }

    // Longer timeout if there's a success image to enjoy
    const celebrationTime = clue.successImage ? 3000 : 900;

    setTimeout(() => {
      setCorrect(false);
      setUserAnswer("");
      setShowHint(false);
      
      // Don't auto-close success image on last clue - let user close it manually
      if (nextIndex < clues.length) {
        setShowSuccessImage(false);
      }

      setCurrentIndex(nextIndex);
      autoSave(
        nextIndex,
        updatedSolved,
        effectiveStartedAt,
        newCompletedAt,
        newActiveTime
      );
    }, celebrationTime);
  }

  // Download case files from CURRENT CLUE
  async function downloadClueCaseFiles() {
    const clue = clues[currentIndex];
    
    if (!clue?.caseFiles || clue.caseFiles.length === 0) {
      alert("No case files available for this clue.");
      return;
    }

    const safeCaseFiles = (clue.caseFiles || []).map((file) => {
      if (!file) return null;
      if (typeof file === "string") return file;
      return file.full || file.url || file.image || file.link || file.thumb || null;
    }).filter(Boolean);

    if (safeCaseFiles.length === 0) {
      alert("No case files available for this clue.");
      return;
    }

    try {
      const zip = new JSZip();
      const folder = zip.folder("case-files");

      // Fetch all files and add to zip
      const fetchPromises = safeCaseFiles.map(async (url, index) => {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          
          // Extract filename from URL or use generic name
          let filename = url.split('/').pop().split('?')[0];
          
          // If filename doesn't have an extension, try to determine from blob type
          if (!filename.includes('.')) {
            const ext = blob.type.split('/')[1] || 'jpg';
            filename = `case-file-${index + 1}.${ext}`;
          }
          
          folder.file(filename, blob);
        } catch (error) {
          console.error(`Error fetching file ${index + 1}:`, error);
        }
      });

      await Promise.all(fetchPromises);

      // Generate zip and download
      const content = await zip.generateAsync({ type: "blob" });
      const trailName = trail?.name || "trail";
      const safeName = trailName.replace(/[^a-z0-9]+/gi, "-");
      saveAs(content, `${safeName}-clue-${clue.order}-case-files.zip`);
      
    } catch (error) {
      console.error("Error creating zip file:", error);
      alert("Error downloading case files. Please try again.");
    }
  }

  async function restartTrail() {
    if (!auth.currentUser) return;
    
    if (!window.confirm("Are you sure you want to restart this trail? All progress will be lost.")) {
      return;
    }
    
    const ref = doc(db, "progress", `${auth.currentUser.uid}_${id}`);
    await setDoc(ref, {
      userId: auth.currentUser.uid,
      trailId: id,
      currentIndex: 0,
      solvedClues: [],
      startedAt: null,
      completedAt: null,
      activeTime: 0,
      lastUpdated: Date.now(),
    });

    // Reload the page to completely reset the state
    window.location.reload();
  }

  if (!authReady) {
    return (
      <div className="starttrail-page">
        <h2>Loading user…</h2>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="starttrail-page">
        <h2>Loading trail…</h2>
      </div>
    );
  }

  if (currentIndex >= clues.length) {
    const elapsedMs =
      startedAt && completedAt ? completedAt - startedAt : 0;

    return (
      <div className="starttrail-page">
        <h1 className="starttrail-title">🎉 Trail Completed!</h1>

        <div style={{ marginBottom: "20px" }}>
          <p>⏱ Elapsed: {formatDuration(elapsedMs)}</p>
          <p>⏳ Active: {formatDuration(activeTime)}</p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <button
            className="starttrail-save-btn"
            onClick={generateCertificate}
            style={{ marginRight: "10px" }}
          >
            🎓 Download Certificate
          </button>
          <button
            className="starttrail-restart-btn"
            onClick={restartTrail}
          >
            🔄 Restart Trail
          </button>
        </div>

        <div className="solved-section">
          <h2>Solved Clues</h2>

          {solvedClues.map((sc, i) => (
            <div key={i} className="solved-clue-card">
              <p>
                <strong>Clue #{sc.order} ✓</strong>
              </p>
              <p>
                <strong>Answer:</strong> {sc.answer}
              </p>

              {sc.helpfultip &&
                sc.helpfultip.trim() !== "" && (
                  <div className="tip-button-area">
                    <button
                      className="info-btn"
                      onClick={() => {
                        setCurrentTip(sc.helpfultip);
                        setCurrentTipClue(sc.order);
                        setShowTipModal(true);
                      }}
                    >
                      Useful Info / Tips for Clue {sc.order}
                    </button>
                  </div>
                )}
            </div>
          ))}
        </div>

        {/* 🎉 SUCCESS IMAGE CELEBRATION MODAL - Also shown on completion screen */}
        {showSuccessImage && successImageUrl && (
          <div
            className="success-image-overlay"
            onClick={() => setShowSuccessImage(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.92)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000,
              animation: "fadeIn 0.3s ease-in",
              padding: "20px"
            }}
          >
            <h2 style={{
              color: "#f6c947",
              fontSize: "2.5em",
              marginBottom: "20px",
              textAlign: "center",
              textShadow: "0 2px 10px rgba(246, 201, 71, 0.5)",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              {currentIndex >= clues.length ? "🎉 Trail Completed! 🎉" : "🎉 Clue Solved! 🎉"}
            </h2>
            
            <div
              style={{
                position: "relative",
                maxWidth: "90%",
                maxHeight: "70vh",
                animation: "zoomIn 0.5s ease-out"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={successImageUrl}
                alt="Success!"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: "12px",
                  boxShadow: "0 10px 40px rgba(246, 201, 71, 0.5)",
                  border: "4px solid #f6c947"
                }}
              />
              <button
                onClick={() => setShowSuccessImage(false)}
                style={{
                  position: "absolute",
                  top: "-15px",
                  right: "-15px",
                  background: "#f6c947",
                  color: "#162220",
                  border: "none",
                  borderRadius: "50%",
                  width: "45px",
                  height: "45px",
                  cursor: "pointer",
                  fontSize: "26px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  transition: "transform 0.2s"
                }}
                onMouseEnter={(e) => e.target.style.transform = "scale(1.1)"}
                onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <p style={{
              color: "white",
              marginTop: "20px",
              fontSize: "1.1em",
              textAlign: "center",
              opacity: 0.8
            }}>
              {currentIndex >= clues.length 
                ? "Congratulations! Click anywhere to see your results" 
                : "Click anywhere to continue"}
            </p>
          </div>
        )}

        {/* ⭐ TIP MODAL - Also available on completion screen */}
        {showTipModal && (
          <div
            className="tip-modal-overlay"
            onClick={() => setShowTipModal(false)}
          >
            <div
              className="tip-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h2>Useful Info / Tips for Clue {currentTipClue}</h2>
              <p>{currentTip}</p>
              <button
                className="tip-close-btn"
                onClick={() => setShowTipModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const clue = clues[currentIndex];
  const progressPercent = Math.round(
    (currentIndex / clues.length) * 100
  );

  return (
    <div className="starttrail-page">
      {showStartButton && (
        <button
          className="starttrail-save-btn"
          onClick={handleStartTimer}
          style={{ marginBottom: "10px" }}
        >
          ▶️ Start Trail Time Now!
        </button>
      )}

      <div className="trail-progress-wrapper">
        <div className="trail-progress-bar">
          <div
            className="trail-progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="trail-progress-text">
          {progressPercent}% Complete
        </div>
      </div>

      {solvedClues.length > 0 && (
        <div className="solved-section">
          <h2>📝 Solved Clues</h2>

          {solvedClues.map((sc, i) => {
            const coords = parseCoords(extractNextLocation(sc));

            const showMap =
              currentIndex < clues.length &&
              i === solvedClues.length - 1 &&
              coords;

            return (
              <div key={i} className="solved-clue-card">
                <p>
                  <strong>Clue #{sc.order} ✓</strong>
                </p>
                <p>
                  <strong>Answer:</strong> {sc.answer}
                </p>

                {sc.helpfultip &&
                  sc.helpfultip.trim() !== "" && (
                    <div className="tip-button-area">
                      <button
                        className="info-btn"
                        onClick={() => {
                          setCurrentTip(sc.helpfultip);
                          setCurrentTipClue(sc.order);
                          setShowTipModal(true);
                        }}
                      >
                        Useful Info / Tips for Clue {sc.order}
                      </button>
                    </div>
                  )}

                {showMap && coords && (
                  <>
                    <p>
                      <strong>Your Next Location: </strong>
                      <a
                        href={buildMapLink(coords)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in Google Maps
                      </a>
                    </p>

                    <iframe
                      src={buildMapEmbed(coords)}
                      title="Next location map"
                      style={{
                        width: "100%",
                        height: "240px",
                        border: "none",
                        borderRadius: "12px",
                        marginTop: "10px",
                      }}
                      loading="lazy"
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
        <h1 className="starttrail-title" style={{ margin: 0 }}>Trail – {trail?.name}</h1>
        <button
          className="help-icon-btn"
          onClick={() => setShowHelpWarning(true)}
          style={{
            background: "none",
            border: "2px solid #f6c947",
            borderRadius: "50%",
            width: "30px",
            height: "30px",
            cursor: "pointer",
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#f6c947",
            fontWeight: "bold"
          }}
          title="Important information about clues"
        >
          ?
        </button>
      </div>

      {/* CASE FILES - FROM CURRENT CLUE */}
      <h2 className="case-title">Case Files for Clue {clue.order}</h2>

      {/* Show download button only if current clue has case files */}
      {clue?.caseFiles && clue.caseFiles.length > 0 && (
        <button
          className="starttrail-save-btn"
          onClick={downloadClueCaseFiles}
          style={{ 
            marginBottom: "15px",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          📥 Download Case Files for This Clue
        </button>
      )}

      {(() => {
        // Use case files from CURRENT CLUE instead of trail
        const safeCaseFiles = (clue.caseFiles || []).map((file) => {
          if (!file) return { full: "", thumb: "" };

          if (typeof file === "string") {
            return { full: file, thumb: file };
          }

          const full =
            file.full ||
            file.url ||
            file.image ||
            file.link ||
            file.thumb ||
            "";

          const thumb =
            file.thumb ||
            file.url ||
            file.image ||
            file.full ||
            file.link ||
            "";

          return { full, thumb };
        });

        return (
          <>
            {safeCaseFiles.length === 0 && (
              <p className="case-empty">No case files available for this clue.</p>
            )}

            <div className="case-file-grid">
              {safeCaseFiles.map((img, i) => (
                <img
                  key={i}
                  src={img.thumb}
                  alt=""
                  className="case-file-thumb"
                  onClick={() => setPopupImage(img.full)}
                  onError={(e) => {
                    e.target.src = img.full;
                  }}
                />
              ))}
            </div>

            {popupImage && (
              <div
                className="popup-overlay"
                onClick={() => setPopupImage(null)}
              >
                <img src={popupImage} className="popup-image" alt="" />
              </div>
            )}
          </>
        );
      })()}

      <p style={{ opacity: 0.7 }}>
        Clue {currentIndex + 1} of {clues.length}
      </p>
      <div className="current-clue-box">
        <p>{clue.question}</p>

        {clue.imageURL && (
          <img
            src={clue.imageURL}
            alt="Clue visual hint"
            className="starttrail-thumb"
            onClick={() => setPopupImage(clue.imageURL)}
          />
        )}

        {clue.hint && (
          <div className="starttrail-hint-wrapper">
            <button
              className="starttrail-hint-btn"
              onClick={() => setShowHint(!showHint)}
            >
              {showHint ? "Hide Hint" : "Show Hint"}
            </button>

            {showHint && (
              <div className="starttrail-hint-box">{clue.hint}</div>
            )}
          </div>
        )}

        <form onSubmit={submitAnswer}>
          <input
            className="starttrail-input"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            placeholder="Type your answer…"
          />
          <button className="starttrail-submit-btn">Submit Answer</button>
        </form>

        {correct && (
          <div className="correct-box">Correct! {clue.answer}</div>
        )}
      </div>

      {popupImage && (
        <div
          className="popup-overlay"
          onClick={() => setPopupImage(null)}
        >
          <img src={popupImage} alt="Enlarged view" className="popup-full" />
        </div>
      )}

      {/* ⭐ GLOBAL MODAL — ALWAYS AVAILABLE */}
      {showTipModal && (
        <div
          className="tip-modal-overlay"
          onClick={() => setShowTipModal(false)}
        >
          <div
            className="tip-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Useful Info / Tips for Clue {currentTipClue}</h2>
            <p>{currentTip}</p>
            <button
              className="tip-close-btn"
              onClick={() => setShowTipModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ⚠️ HELP WARNING MODAL */}
      {showHelpWarning && (
        <div
          className="tip-modal-overlay"
          onClick={() => setShowHelpWarning(false)}
        >
          <div
            className="tip-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: "#f6c947" }}>⚠️ Important Information</h2>
            <p style={{ lineHeight: "1.6" }}>
              Please note that some clues may be obstructed depending on the time of year you're doing the trail, due to events being held in the area.
            </p>
            <p style={{ lineHeight: "1.6", marginTop: "10px" }}>
              If you encounter any difficulties locating a clue, please use the hint button or contact support.
            </p>
            <button
              className="tip-close-btn"
              onClick={() => setShowHelpWarning(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* 🎉 SUCCESS IMAGE CELEBRATION MODAL */}
      {showSuccessImage && successImageUrl && (
        <div
          className="success-image-overlay"
          onClick={() => setShowSuccessImage(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.92)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            animation: "fadeIn 0.3s ease-in",
            padding: "20px"
          }}
        >
          <h2 style={{
            color: "#f6c947",
            fontSize: "2.5em",
            marginBottom: "20px",
            textAlign: "center",
            textShadow: "0 2px 10px rgba(246, 201, 71, 0.5)",
            animation: "pulse 1.5s ease-in-out infinite"
          }}>
            {currentIndex + 1 >= clues.length ? "🎉 Trail Completed! 🎉" : "🎉 Clue Solved! 🎉"}
          </h2>
          
          <div
            style={{
              position: "relative",
              maxWidth: "90%",
              maxHeight: "70vh",
              animation: "zoomIn 0.5s ease-out"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={successImageUrl}
              alt="Success!"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 10px 40px rgba(246, 201, 71, 0.5)",
                border: "4px solid #f6c947"
              }}
            />
            <button
              onClick={() => setShowSuccessImage(false)}
              style={{
                position: "absolute",
                top: "-15px",
                right: "-15px",
                background: "#f6c947",
                color: "#162220",
                border: "none",
                borderRadius: "50%",
                width: "45px",
                height: "45px",
                cursor: "pointer",
                fontSize: "26px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                transition: "transform 0.2s"
              }}
              onMouseEnter={(e) => e.target.style.transform = "scale(1.1)"}
              onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
              title="Close"
            >
              ×
            </button>
          </div>
          
          <p style={{
            color: "white",
            marginTop: "20px",
            fontSize: "1.1em",
            textAlign: "center",
            opacity: 0.8
          }}>
            {currentIndex + 1 >= clues.length 
              ? "Congratulations! Click anywhere to see your results" 
              : "Click anywhere to continue"}
          </p>
        </div>
      )}

    </div>
  );
}